/**
 * PhotoCaptureField — Wave 5.6 (2026-02).
 *
 * Jobsite-first photo capture control. On mobile, offers TWO clear
 * buttons instead of a single input:
 *   1. "Take photo"    → <input type=file accept="image/*" capture="environment">
 *   2. "Choose existing" → <input type=file accept="image/*"> (no capture)
 *
 * A single input with `capture` opens the camera ONLY on iOS/some
 * Android builds, hiding the photo-library option entirely. The two-
 * button pattern (proven in `AttachPickerModal` and the site-visit
 * capture flow) exposes both paths explicitly and works cross-device.
 *
 * Props:
 *   onFile(file, source)  where source ∈ 'camera' | 'library'
 *   preview               object URL for the currently-selected file
 *   onClear               clears the current selection
 *   compact               tighter dark styling for inline forms
 *   testIdBase            used to name buttons/inputs deterministically
 *   accept                file types (default 'image/*')
 */
import React, { useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, ImageIcon, X } from 'lucide-react';

export default function PhotoCaptureField({
  onFile,
  preview,
  onClear,
  disabled = false,
  compact = false,
  darkMode = false,
  testIdBase = 'photo-capture',
  accept = 'image/*',
  takeLabel = 'Take photo',
  libraryLabel = 'Choose existing',
}) {
  const cameraRef = useRef(null);
  const libraryRef = useRef(null);

  const handle = (source) => (e) => {
    const file = e.target.files?.[0];
    if (file && onFile) onFile(file, source);
    // Reset so re-picking the same file re-fires onChange.
    e.target.value = '';
  };

  if (preview) {
    return (
      <div className="relative rounded overflow-hidden border border-slate-300 dark:border-[#3A3A3A]" data-testid={`${testIdBase}-preview`}>
        <img src={preview} alt="Selected" className="w-full max-h-64 object-cover" />
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="absolute top-1 right-1 bg-black/70 text-white text-[11px] px-2 py-1 rounded flex items-center gap-1"
            data-testid={`${testIdBase}-clear`}
          >
            <X className="w-3 h-3" /> Remove
          </button>
        )}
      </div>
    );
  }

  // Wave 5.9 build 8 — theme-aware capture button styles. Compact +
  // darkMode gives the old obsidian style; compact + light-mode gives
  // a proper light-theme outline that stays readable in shared forms.
  const btnCls = compact
    ? (darkMode
        ? 'gap-2 min-h-[48px] border-[#3A3A3A] bg-[#0D0D0D] hover:bg-[#161616] text-[#E5E2DD]'
        : 'gap-2 min-h-[48px] border-slate-300 bg-white hover:bg-slate-50 text-slate-800')
    : 'gap-2 min-h-[52px]';

  return (
    <div className="grid grid-cols-2 gap-2" data-testid={testIdBase}>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => cameraRef.current?.click()}
        className={btnCls}
        data-testid={`${testIdBase}-take-btn`}
      >
        <Camera className="w-5 h-5" />
        <span className="text-sm">{takeLabel}</span>
      </Button>
      <Button
        type="button"
        variant="outline"
        disabled={disabled}
        onClick={() => libraryRef.current?.click()}
        className={btnCls}
        data-testid={`${testIdBase}-library-btn`}
      >
        <ImageIcon className="w-5 h-5" />
        <span className="text-sm">{libraryLabel}</span>
      </Button>

      <input
        ref={cameraRef}
        type="file"
        accept={accept}
        capture="environment"
        className="hidden"
        onChange={handle('camera')}
        data-testid={`${testIdBase}-camera-input`}
      />
      <input
        ref={libraryRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={handle('library')}
        data-testid={`${testIdBase}-library-input`}
      />
    </div>
  );
}
